import React, { memo, useState, useEffect, useCallback } from 'react';

import _debounce from 'lodash/debounce';
import { FaPlay, FaPause } from 'react-icons/fa6';
import { GoUnmute } from 'react-icons/go';
import { BiSolidVolumeMute } from 'react-icons/bi';
import { CgArrowsExpandRight } from 'react-icons/cg';

import './style.scss';

const PortfolioItem = memo(
  ({ isMobile, videoRef, type, image, order, username, index, currentlyPlaying, handlePlay, handlePause, realActiveIndex }) => {
    const [isPlaying, setPlaying] = useState(false);
    const [isMuted, setMuted] = useState(true);
    const [isFullScreen, setFullScreen] = useState(false);

    useEffect(() => {
      const playCurrentMobileVideo = _debounce(() => {
        const video = videoRef?.current;
        if (isMobile && type === 'video' && video && index === realActiveIndex) {
          try {
            if (video?.play) {
              handlePlay(index);
              video?.play();
              setPlaying(true);
            }
          } catch (videoError) {
            console.error('Video Play Error Occur, Error: ', videoError);
          }
        }
      }, 1000);

      playCurrentMobileVideo();

      return () => {
        playCurrentMobileVideo.cancel();
      };
    }, [realActiveIndex, index, videoRef, type, handlePlay, isMobile]);

    const setVideoPlayPause = useCallback(() => {
      const video = videoRef?.current;
      if (type === 'video' && video) {
        if (currentlyPlaying.length < 1) {
          if (isPlaying) {
            video?.pause();
            setPlaying(false);
          }
          return;
        }
        const isFound = currentlyPlaying.includes(index);
        setPlaying((isP) => {
          if (!isFound) {
            if (isP) {
              video?.pause();
            }
          } else {
            try {
              if (video?.play) {
                video?.play();
              }
            } catch (videoError) {
              console.error('Video Play Error Occur, Error: ', videoError);
            }
          }
          return isFound;
        });
      }
    }, [currentlyPlaying, index, isPlaying, type, videoRef]);

    useEffect(() => {
      const handleSetMobilePlayPause = _debounce(setVideoPlayPause, 100);
      handleSetMobilePlayPause();

      return () => {
        handleSetMobilePlayPause.cancel();
      };
    }, [setVideoPlayPause]);

    const handleFullscreenChange = useCallback(() => {
      const fsElement = document.fullscreenElement || document.webkitFullscreenElement;
      setFullScreen(fsElement === videoRef?.current);
    }, [videoRef]);

    // Additional handlers or checks for iOS behavior
    const handleIOSBehavior = useCallback(() => {
      if (document.visibilityState === 'visible') {
        // Check if fullscreen is still active when the document is visible again
        const isFullscreen = document.fullscreenElement || document.webkitFullscreenElement;
        if (!isFullscreen) {
          setFullScreen(false);
        }
      }
    }, []);

    const handleVideoPlay = useCallback(() => handlePlay(index), [handlePlay, index]);

    const handleVideoPause = useCallback(() => handlePause(index), [handlePause, index]);

    const exitWebKitFullScreen = useCallback(() => setFullScreen(false), []);

    useEffect(() => {
      const video = videoRef?.current;
      if (type === 'video') {
        if (isMobile && index === realActiveIndex) {
          document.addEventListener('fullscreenchange', handleFullscreenChange);
          document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
          document.addEventListener('visibilitychange', handleIOSBehavior);

          if (video) {
            // Attach event listeners
            video.addEventListener('play', handleVideoPlay);
            video.addEventListener('pause', handleVideoPause);
            video.addEventListener('webkitendfullscreen', exitWebKitFullScreen);
          }
        }

        if (!isMobile) {
          document.addEventListener('fullscreenchange', handleFullscreenChange);
          document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
          document.addEventListener('visibilitychange', handleIOSBehavior);

          if (video) {
            // Attach event listeners
            video.addEventListener('play', handleVideoPlay);
            video.addEventListener('pause', handleVideoPause);
            video.addEventListener('webkitendfullscreen', exitWebKitFullScreen);
          }
        }
      }

      return () => {
        if (type === 'video') {
          document.removeEventListener('fullscreenchange', handleFullscreenChange);
          document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
          document.removeEventListener('visibilitychange', handleIOSBehavior);
          if (video) {
            // Cleanup function to remove event listeners
            video.removeEventListener('play', handleVideoPlay);
            video.removeEventListener('pause', handleVideoPause);
            video.removeEventListener('webkitendfullscreen', exitWebKitFullScreen);
          }
        }
      };
    }, [
      exitWebKitFullScreen,
      handleFullscreenChange,
      handleIOSBehavior,
      handlePause,
      handlePlay,
      handleVideoPause,
      handleVideoPlay,
      index,
      isMobile,
      realActiveIndex,
      type,
      videoRef,
    ]);

    const togglePlayPause = useCallback(() => {
      const video = videoRef?.current;
      if (video) {
        if (video?.paused || video?.ended) {
          try {
            if (video?.play) {
              handlePlay(index);
              video?.play();
            }
          } catch (videoError) {
            console.error('Video Play Error Occur, Error: ', videoError);
          }
        } else {
          handlePause(index);
          video?.pause();
        }
      }
    }, [handlePause, handlePlay, index, videoRef]);

    const toggleMute = useCallback(() => {
      const video = videoRef?.current;
      if (video) {
        video.muted = !video?.muted;
        setMuted(video?.muted);
      }
    }, [videoRef]);

    const enterFullscreen = useCallback(() => {
      const video = videoRef?.current;
      if (video) {
        if (video.requestFullscreen) {
          video.requestFullscreen();
        } else if (video.webkitRequestFullscreen) {
          video.webkitRequestFullscreen();
        } else if (video.webkitEnterFullscreen) {
          video.webkitEnterFullscreen();
        }
        setFullScreen(true);
      }
    }, [videoRef]);

    const handleVideoEnd = useCallback(() => handlePause(index), [handlePause, index]);

    if (type === 'image') {
      return (
        <>
          <img src={image} alt={order} className='swiper-item' width={'100%'} height={isMobile ? 240 : 220} />
          {username && <p className='username-text'>@{username}</p>}
        </>
      );
    }

    return (
      <>
        <div className='videoWrapper' style={{ height: isMobile ? 240 : 220 }}>
          <video
            autoPlay={currentlyPlaying.includes(index)}
            poster={`${image}.jpg`}
            playsInline={!isFullScreen}
            ref={videoRef}
            controls={isFullScreen}
            preload='metadata'
            loading='lazy'
            className='swiper-item'
            style={{ objectFit: isFullScreen ? 'contain' : 'cover' }}
            height={isMobile ? 240 : 220}
            width={isMobile ? 240 : 220}
            disablePictureInPicture
            controlsList='nodownload noplaybackrate'
            muted={isMuted}
            onEnded={handleVideoEnd}>
            <source src={`${image}#t=0.001`} type='video/mp4' />
          </video>
          <div className='iconWrapper playIcon' onClick={togglePlayPause}>
            <Choose>
              <When condition={isPlaying}>
                <FaPause color='white' size={11} />
              </When>
              <Otherwise>
                <FaPlay color='white' size={11} />
              </Otherwise>
            </Choose>
          </div>
          <div className='controlWrapper'>
            <div className='iconWrapper' onClick={toggleMute}>
              <Choose>
                <When condition={isMuted}>
                  <BiSolidVolumeMute color='white' size={18} />
                </When>
                <Otherwise>
                  <GoUnmute color='white' size={18} />
                </Otherwise>
              </Choose>
            </div>
            <div className='iconWrapper' onClick={enterFullscreen}>
              <CgArrowsExpandRight color='white' size={15} />
            </div>
          </div>
        </div>
        {username && <p className='username-text'>@{username}</p>}
      </>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps?.isMobile === nextProps?.isMobile &&
      prevProps?.videoRef === nextProps?.videoRef &&
      prevProps?.type === nextProps?.type &&
      prevProps?.image === nextProps?.image &&
      prevProps?.order === nextProps?.order &&
      prevProps?.username === nextProps?.username &&
      prevProps?.index === nextProps?.index &&
      prevProps?.currentlyPlaying === nextProps?.currentlyPlaying &&
      prevProps?.handlePlay === nextProps?.handlePlay &&
      prevProps?.handlePause === nextProps?.handlePause &&
      prevProps?.realActiveIndex === nextProps?.realActiveIndex
    );
  }
);

export default PortfolioItem;
